/* eslint-disable react/no-danger */
import React, { useCallback, useMemo, useState } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from '../../Button';
import ConsentInfo from '../../Consents/ConsentInfo';
import Icon from '../../Icon';
import SignConsentButton from '../../Healthboard/Components/VisitConsents/ConsentDetailsPopup/SingConsentButton';
import useConsentOutcomesInfo from '../../../hooks/consents/useConsentOutcomesInfo';
import { formatDayMonth } from '../../../utils/converters/dateConverters';
import { virtualVisitActions } from '../../../redux/healthboard/virtual-visit';

import styles from './styles.styl';

const SignConsent = ({ appointment, onDecline, loading, onSign }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const userId = get(appointment, 'userId');
  const consents = get(appointment, 'consentsAssignment.consents') || [];
  const total = get(appointment, 'consentsAssignment.consentsCount') || 0;
  const startTime = get(appointment, 'startTime');

  const [consentIndex, setConsentIndex] = useState(() =>
    consents.findIndex(c => !c.accepted),
  );

  const rawConsent = useMemo(() => consents[consentIndex], [
    consentIndex,
    consents,
  ]);

  const consent = useMemo(
    () => ({
      name: get(rawConsent, 'name'),
      typeName: get(rawConsent, 'consentType.name'),
      versionTitle: formatMessage({ id: 'common.version' }),
      version: get(rawConsent, 'version'),
      content: get(rawConsent, 'content'),
      multipleOutcomes: get(rawConsent, 'multipleOutcomes', false),
      outcomeTitles: get(rawConsent, 'outcomeTitles', []),
    }),
    [rawConsent],
  );

  const {
    buttonAccept,
    consentOutcomeTitles,
    isConsentHasMultipleOutcomes,
  } = useConsentOutcomesInfo(consent);

  const formattedDate = useMemo(() => formatDayMonth(startTime), [startTime]);

  const counter = `${consentIndex + 1}/${total}`;

  const handleConfirm = useCallback(
    ({ outcomeTitle } = {}) => {
      // eslint-disable-next-line promise/catch-or-return
      onSign(rawConsent, userId, outcomeTitle).then(res => {
        const index = consentIndex + 1;
        if (consents[index]) {
          setConsentIndex(index);
        } else {
          dispatch(virtualVisitActions.setVisibility(true));
        }
        return res;
      });
    },
    [rawConsent, consentIndex, userId],
  );

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.info}>
          {formatMessage({ id: 'appointment.consent.titleMsg' })}
        </div>
        <div className={styles.consent}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Icon
                name="icon-consent"
                size={36}
                className={styles.headerIcon}
              />
              <div className={styles.consentTypeName}>{consent.typeName}</div>
            </div>
            {formattedDate && (
              <div className={styles.headerDate}>{formattedDate}</div>
            )}
          </div>
          <div className={styles.consentBody}>
            <ConsentInfo consent={consent} counter={counter} />
          </div>
          <div className={styles.footer}>
            {onDecline && (
              <Button
                raised
                type="button"
                color="accent"
                onClick={onDecline}
                className={styles.button}
              >
                {formatMessage({ id: 'common.decline' })}
              </Button>
            )}
            <SignConsentButton
              buttonAccept={buttonAccept}
              handleAcceptConsent={handleConfirm}
              isConsentHasMultipleOutcomes={isConsentHasMultipleOutcomes}
              isSignConsentLoading={loading}
              titles={consentOutcomeTitles}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

SignConsent.propTypes = {
  appointment: PropTypes.object,
  loading: PropTypes.bool,
  onSign: PropTypes.func,
  onDecline: PropTypes.func,
};

export default SignConsent;



// WEBPACK FOOTER //
// ./src/components/VirtualRoom/SignConsent/index.jsx